import { Combobox, ComboboxInput, ComboboxOptions, Description, Label } from '@headlessui/react';
import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';
import { type ChangeEvent, type MouseEvent, useState } from 'react';
import InputError from '~/components/feedback/InputError';
import type { IDealerInput, IDropdownOption, ILookupInput } from '~/types/inputs';
import { getInputSizeClasses } from '~/utils/form';
import Option, { NullOption } from './Option';
import type { HighlitOption } from './types';
import { MdClose } from 'react-icons/md/index';
import '../AusPostcode/style.css';
import SimpleInput from '../SimpleInput';
import './style.css';

function setHighlightIndices(option: IDropdownOption, searchValue: string): HighlitOption {
	const startIndex = option.label.toLowerCase().indexOf(searchValue.toLowerCase());
	const endIndex = startIndex + searchValue.length;

	return {
		...option,
		startIndex,
		endIndex,
	};
}

export default function Dealer({
	name,
	label,
	width,
	type,
	required,
	options,
	description,
	...rest
}: IDealerInput) {
	const [value, setValue] = useState('');
	const [filteredOptions, setFilteredOptions] = useState<HighlitOption[]>([]);
	const [selectedOption, setSelectedOption] = useState<IDropdownOption | null>(null);
	const [manualMode, setManualMode] = useState(false);
	const [field, meta] = useField(name);
	const { submitCount, setFieldValue } = useFormikContext();
	const isError = (meta.touched || submitCount > 0) && !!meta.error;

	const resetField = (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		setValue('');
		setFieldValue(name, '');
		setSelectedOption(null);
		setFilteredOptions([]);
		setManualMode(false);
		setFieldValue('dealer:manualMode', false);
	};

	const handleSelection = (option: IDropdownOption | null) => {
		setValue('');
		setFilteredOptions([]);

		if (option === null) {
			return;
		}

		if (option.value === 'Other') {
			setFieldValue('dealer:manualMode', true);
			setFieldValue('dealer_other', '');
			setManualMode(true);
			setFieldValue(name, 'Other');
			return;
		}

		setSelectedOption(option);
		setFieldValue(name, option.value);
	};

	const onInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value;

		setValue(newValue);

		// If we are changing the internal value (i.e. search value) and there's currently no search value,
		// Clear what is in Formik state as this means we are initiating a new search flow
		if (!value) {
			setFieldValue(name, '');
			setSelectedOption(null);
		}

		if (!newValue) {
			setFilteredOptions([]);
		}

		const results = options.reduce<{
			startMatches: HighlitOption[];
			fuzzyMatches: HighlitOption[];
		}>(
			(acc, curr) => {
				const parsedLabel = curr.label.toLowerCase();
				const parsedValue = newValue.toLowerCase();

				if (parsedLabel.startsWith(parsedValue)) {
					return {
						startMatches: [...acc.startMatches, setHighlightIndices(curr, newValue)],
						fuzzyMatches: acc.fuzzyMatches,
					};
				}

				if (parsedLabel.includes(parsedValue)) {
					return {
						startMatches: acc.startMatches,
						fuzzyMatches: [...acc.fuzzyMatches, setHighlightIndices(curr, newValue)],
					};
				}

				return acc;
			},
			{
				startMatches: [],
				fuzzyMatches: [],
			},
		);

		setFilteredOptions([...results.startMatches, ...results.fuzzyMatches].slice(0, 10));
	};

	if (manualMode) {
		return (
			<SimpleInput
				label="Other Dealer (not in list)"
				name="dealer_other"
				required
				width="full"
				type="text"
				description=""
			/>
		);
	}

	return (
		<Combobox
			className={clsx(
				getInputSizeClasses(width),
				'mx-auto simple-input-container rounded-tl-input rounded-tr-input relative h-input w-full',
			)}
			as="div"
			value={field.value}
			onChange={handleSelection}
		>
			<div className="mx-auto relative h-input w-full">
				{selectedOption && (
					<span className="lookup-value absolute w-full flex items-center justify-between bottom-2 left-0 px-5">
						<span className="pointer-events-none whitespace-nowrap overflow-hidden overflow-ellipsis font-body-large w-full outline-none text-left">
							{selectedOption.label}
						</span>
						<button onClick={resetField} className="lookup-clear-button">
							<MdClose size={20} />
						</button>
					</span>
				)}
				<ComboboxInput
					{...rest}
					value={value}
					onChange={onInputChange}
					type={type}
					className="simple-input-input lookup-input peer placeholder-transparent text-base w-full outline-none pl-5 pr-5 pt-6 pb-1 h-full"
					id={name}
					aria-invalid={isError}
					aria-describedby={`${name}_error`}
					placeholder=" "
				/>
				<Label
					htmlFor={name}
					className={clsx(
						'simple-input-label pointer-events-none transition-all peer-focus:top-3 top-3 peer-focus:text-xs text-xs absolute left-5',
						field.value
							? 'top-3 text-xs'
							: 'peer-placeholder-shown:top-6 peer-placeholder-shown:font-large-body font-large-body',
					)}
				>
					{label}
					{required && !label.endsWith('*') ? <sup>*</sup> : null}
				</Label>
				<ComboboxOptions className="absolute translate-y-full w-full bottom-[-2px] z-20">
					{filteredOptions.map(option => {
						return <Option option={option} key={option.value} />;
					})}
					{value.length >= 2 && <NullOption />}
				</ComboboxOptions>
				<InputError name={name} isError={isError}>
					{meta.error}
				</InputError>
			</div>
			{description && (
				<Description className="font-small-body text-left mt-1">{description}</Description>
			)}
		</Combobox>
	);
}
